import {
  initAnalytics,
  setAnalyticsUser,
  setAnalyticsUserProperties,
  clearAnalyticsUser
} from '@cw-elements/analytics-provider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { getLocale } from 'utils/getDatePickerLocale';
import { useCookieConsent } from 'utils/custom-hooks/use-cookie-consent.hook';
import { useSelector } from 'react-redux';
import { userProfileSelector } from 'app/header/header.selector';

type AnalyticsInstance = {
  add: (plugin: any) => void;
  setUserId: (userId: string | undefined) => Promise<void>;
  reset: () => Promise<void>;
};

export function useInitAnalytics(hasLaunchDarklyStarted = false) {
  const { accountsCws15893EnableCookieConsent } = useFlags();
  const { cookieBotConsentStatistics } = useCookieConsent();
  const analyticsInstanceRef = useRef<AnalyticsInstance | null>(null);
  const user = useSelector(userProfileSelector);

  useEffect(() => {
    const initializeAnalytics = async (): Promise<void> => {
      if (!process.env.REACT_APP_AMPLITUDE_KEY) {
        console.warn('Amplitude API key is not configured');
        return;
      }

      if (analyticsInstanceRef.current) {
        return; // Already initialized
      }

      try {
        const analyticsInstance = await initAnalytics({
          amplitude: {
            clientId: process.env.REACT_APP_AMPLITUDE_KEY,
            sendIpAddressOnConsent: true
          },
          consent: { enabled: true }
        });
        const engPlugin = engagementPlugin({
          locale: getLocale()?.locale
        });

        analyticsInstance.add(engPlugin);
        analyticsInstanceRef.current = analyticsInstance;
        console.info('Analytics initialized successfully');
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
      }
    };

    const setUserData = async (): Promise<void> => {
      if (!user?.id) return;

      try {
        setAnalyticsUser(user.id);
        setAnalyticsUserProperties({
          jobTitle: user.job_title,
          email: user.email
        });
        console.info('User data set in analytics');
      } catch (error) {
        console.error('Failed to set user data:', error);
      }
    };

    const removeUserData = async (): Promise<void> => {
      if (!analyticsInstanceRef.current) return;

      try {
        clearAnalyticsUser();
        console.info('User data removed from analytics');
      } catch (error) {
        console.error('Failed to remove user data:', error);
      }
    };

    const hasUserConsent = (): boolean => {
      if (!hasLaunchDarklyStarted) {
        return false;
      }
      if (accountsCws15893EnableCookieConsent) {
        return cookieBotConsentStatistics;
      }
      return true;
    };

    const manageAnalytics = async (): Promise<void> => {
      if (!hasLaunchDarklyStarted) {
        return;
      }
      await initializeAnalytics();

      if (hasUserConsent()) {
        await setUserData();
      } else {
        await removeUserData();
      }
    };

    manageAnalytics();
  }, [
    cookieBotConsentStatistics,
    accountsCws15893EnableCookieConsent,
    hasLaunchDarklyStarted,
    user?.id,
    user?.job_title,
    user?.email
  ]);
}
